// packages/web-ui/src/state/files.ts
//
// Story W3-S5 — `files` zustand slice. Owns the file-tree consumer state for
// the Files ContextPanel:
//
//   - `tree`            — FileTreeNode[] from `filesApi.getTree`.
//   - `loading`         — initial-fetch in-flight flag.
//   - `lastError`       — last `ApiError` from a `getTree` / `rename` call;
//                         consumers surface as the `[Retry]` error state.
//   - `expansionState`  — `Map<path, boolean>` driving the chevron toggle.
//   - `pulsedPaths`     — `Map<path, expiresAt: number>` for the 1 s pulse
//                         after `files:changed` (AC #6). Cleanup is owned by
//                         `prunePulses(now)` — consumers call it from a
//                         `setInterval` while the map is non-empty.
//   - `pendingRenames`  — `Set<path>` rows awaiting a rename Ok / SSE refresh.
//   - `chipFilter`      — current chip-filter selection.
//   - `query`           — debounced search query (consumer owns the debounce
//                         timer; this slice stores the already-debounced
//                         value).
//   - `selectedPath`    — currently keyboard-focused row.
//
// The slice is exposed as a vanilla store so consumers can subscribe outside
// React (and so tests can isolate state without touching the root store). A
// React hook re-exports a `useSyncExternalStore` adapter.
//
// Coding-standards notes (CLAUDE.md):
// - All public functions return `Result<T, E>` at the API boundary.
// - The slice itself uses imperative actions (zustand convention) — no throws.
// - `Map<path, ...>` is preserved by reference where mutations occur via
//   `new Map(prev)` clones to keep zustand's subscriber-equality contract.

import { useStore } from 'zustand';
import { createStore, type StoreApi } from 'zustand/vanilla';
import type { Result } from '@tessa-lean/base';
import {
  filesApi,
  type FileTreeNode,
  type GetTreeInput,
  type RenameFileInput,
  type RenameFileResponse,
} from '../api/clients/files.js';
import type { ApiContext, ApiError } from '../api/client.js';

/** Default 1 s pulse duration per AC #6.1 — exported for test injection. */
export const PULSE_DURATION_MS = 1000;

export type ChipFilter = 'All' | '.sysml' | '.json' | '.md';

export const CHIP_FILTERS: readonly ChipFilter[] = [
  'All',
  '.sysml',
  '.json',
  '.md',
] as const;

export interface FilesSliceState {
  tree: FileTreeNode[];
  loading: boolean;
  lastError: ApiError | null;
  expansionState: ReadonlyMap<string, boolean>;
  pulsedPaths: ReadonlyMap<string, number>;
  pendingRenames: ReadonlySet<string>;
  chipFilter: ChipFilter;
  query: string;
  selectedPath: string | null;
}

export const FILES_INITIAL_STATE: FilesSliceState = {
  tree: [],
  loading: false,
  lastError: null,
  expansionState: new Map(),
  pulsedPaths: new Map(),
  pendingRenames: new Set(),
  chipFilter: 'All',
  query: '',
  selectedPath: null,
};

export interface FilesSliceActions {
  /** Issue `filesApi.getTree(input)`; sets `tree`/`lastError` accordingly. */
  loadTree: (input?: GetTreeInput) => Promise<Result<FileTreeNode[], ApiError>>;
  /** Toggle / set the expansion state for a single dir path. */
  setExpanded: (path: string, expanded: boolean) => void;
  /** Convenience: flip current state. Returns the new value. */
  toggleExpanded: (path: string) => boolean;
  /** Pulse the row for `path` for `PULSE_DURATION_MS` ms (or `now + ttlMs`). */
  pulsePath: (path: string, now?: number, ttlMs?: number) => void;
  /** Drop pulse entries whose `expiresAt <= now`. */
  prunePulses: (now: number) => void;
  /** Update the chip-filter selection. */
  setChipFilter: (chip: ChipFilter) => void;
  /** Update the (already-debounced) search query. */
  setQuery: (query: string) => void;
  /** Update the keyboard-focused row. */
  setSelectedPath: (path: string | null) => void;
  /** Mark a path as awaiting rename Ok / SSE refresh. */
  setPendingRename: (path: string, pending: boolean) => void;
  /** Issue `filesApi.rename(input)`; pendingRenames flips on/off around it. */
  renamePath: (input: RenameFileInput) => Promise<Result<RenameFileResponse, ApiError>>;
  /** Replace the tree wholesale (used after SSE-driven refetches). */
  setTree: (tree: FileTreeNode[]) => void;
  /** Test-helper — reset slice to defaults. Action functions preserved. */
  _reset: () => void;
}

export type FilesSlice = FilesSliceState & FilesSliceActions;

export interface FilesSliceConfig {
  apiContext?: ApiContext;
  /** Override `filesApi` for test seams. */
  api?: Pick<typeof filesApi, 'getTree' | 'rename'>;
  /** Inject `Date.now` for deterministic pulse-expiry tests. */
  now?: () => number;
}

function defaultApiContext(): ApiContext {
  const token =
    typeof localStorage !== 'undefined' ? localStorage.getItem('tessaLeanToken') : null;
  return { baseUrl: '', token };
}

/**
 * Build a vanilla zustand store backing a single `<FilesPanel>` mount.
 * Most consumers will instead use `useFilesStore` (singleton) — this factory
 * exists so tests can isolate state without resetting a global.
 */
export function createFilesStore(config: FilesSliceConfig = {}): StoreApi<FilesSlice> {
  const ctx = config.apiContext ?? defaultApiContext();
  const api = config.api ?? filesApi;
  const now = config.now ?? (() => Date.now());

  return createStore<FilesSlice>((set, get) => ({
    ...FILES_INITIAL_STATE,

    loadTree: async (input = {}) => {
      set((s) => ({ ...s, loading: true, lastError: null }));
      const res = await api.getTree(ctx, input);
      if (res.ok) {
        set((s) => ({ ...s, loading: false, tree: res.value, lastError: null }));
      } else {
        set((s) => ({ ...s, loading: false, lastError: res.error }));
      }
      return res;
    },

    setExpanded: (path, expanded) => {
      set((s) => {
        const next = new Map(s.expansionState);
        next.set(path, expanded);
        return { ...s, expansionState: next };
      });
    },

    toggleExpanded: (path) => {
      const cur = get().expansionState.get(path) ?? false;
      const nextVal = !cur;
      set((s) => {
        const next = new Map(s.expansionState);
        next.set(path, nextVal);
        return { ...s, expansionState: next };
      });
      return nextVal;
    },

    pulsePath: (path, atNow, ttlMs) => {
      const t = atNow ?? now();
      const ttl = ttlMs ?? PULSE_DURATION_MS;
      set((s) => {
        const next = new Map(s.pulsedPaths);
        next.set(path, t + ttl);
        return { ...s, pulsedPaths: next };
      });
    },

    prunePulses: (atNow) => {
      const cur = get().pulsedPaths;
      let dirty = false;
      const next = new Map(cur);
      for (const [path, expiresAt] of cur) {
        if (expiresAt <= atNow) {
          next.delete(path);
          dirty = true;
        }
      }
      if (dirty) set((s) => ({ ...s, pulsedPaths: next }));
    },

    setChipFilter: (chip) => set((s) => ({ ...s, chipFilter: chip })),

    setQuery: (query) => set((s) => ({ ...s, query })),

    setSelectedPath: (path) => set((s) => ({ ...s, selectedPath: path })),

    setPendingRename: (path, pending) => {
      set((s) => {
        const next = new Set(s.pendingRenames);
        if (pending) next.add(path);
        else next.delete(path);
        return { ...s, pendingRenames: next };
      });
    },

    renamePath: async (input) => {
      get().setPendingRename(input.path, true);
      const res = await api.rename(ctx, input);
      get().setPendingRename(input.path, false);
      if (!res.ok) {
        set((s) => ({ ...s, lastError: res.error }));
      }
      return res;
    },

    setTree: (tree) => set((s) => ({ ...s, tree })),

    _reset: () =>
      set(() => ({
        ...FILES_INITIAL_STATE,
        expansionState: new Map(),
        pulsedPaths: new Map(),
        pendingRenames: new Set(),
      })),
  }));
}

/**
 * Module-singleton store. Most production callers should use this — the
 * factory above (`createFilesStore`) is for test isolation.
 */
let _singleton: StoreApi<FilesSlice> | null = null;

export function getFilesStore(): StoreApi<FilesSlice> {
  _singleton ??= createFilesStore();
  return _singleton;
}

/** Test-only — drops the singleton so the next call rebuilds with defaults. */
export function _resetFilesStoreForTests(): void {
  _singleton = null;
}

/**
 * Default identity selector — module-level constant so the same reference
 * is reused across renders (preserves the previous "non-test consumers
 * re-render on every slice update" semantics without re-allocating a
 * closure every call).
 */
function selectIdentity<T>(s: FilesSlice): T {
  return s as unknown as T;
}

/**
 * React adapter — selector-based subscription via zustand's `useStore`. The
 * pre-MED-13 implementation handed two fresh inline closures to
 * `useSyncExternalStore` on every render (`() => sel(store.getState())`),
 * which is the canonical "non-stable getter" trap. zustand's `useStore`
 * accepts the selector directly and memoises the subscriber/snapshot pair
 * internally.
 *
 * Selectors should still return shallow-stable references; the default
 * identity selector returns the entire slice (re-renders on every update —
 * fine for small consumers).
 */
export function useFilesStore(): FilesSlice;
export function useFilesStore<T>(selector: (s: FilesSlice) => T): T;
export function useFilesStore<T>(selector?: (s: FilesSlice) => T): T | FilesSlice {
  const store = getFilesStore();
  return useStore(store, selector ?? selectIdentity) as T;
}
